import { SearchIcon } from "@chakra-ui/icons";
import { Box, Button, Flex, Grid, GridItem, Input, InputGroup, InputLeftElement, Text } from "@chakra-ui/react";
import { AgentTaskMemoryClickPayload, AgentTaskProgress, FencyProvider } from "@fencyai/react";
import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { useCreateMemoryDownloadLinkMutation } from "../../../autogen/bff-api";
import { useLoggedInWithOrgContextState } from "../../../common/auth/useLoggedInWithOrgContextState";
import { ContractSearchResultModal } from "./ContractSearchResultModal";
import { useApiError } from "../../../common/errors/useApiError";
import { fencyPromise } from "../../../common/fency/fencySetup";
import { useFency } from "../../../common/fency/useFency";
import { useMemorySearch } from "../../../common/fency/useMemorySearch";
import { Layout } from "../../../common/layout/Layout";
import { CONTENT_WIDTH } from "../../../config";

type ContractSearchInnerProps = {
  fetchCreateContractSearchClientToken: () => Promise<{ clientToken: string }>;
};

const ContractSearchInner = ({ fetchCreateContractSearchClientToken }: ContractSearchInnerProps) => {
  const { t } = useTranslation();
  const authState = useLoggedInWithOrgContextState();
  const apiError = useApiError();
  const [query, setQuery] = useState("");
  const [createMemoryDownloadLink] = useCreateMemoryDownloadLinkMutation();
  const [resultModal, setResultModal] = useState<{
    downloadLink: string;
    initialPage: number;
    memoryId: string;
    memoryTitle: string;
    searchQuery: string;
  } | null>(null);
  const { latest, isSearching, search } = useMemorySearch({
    fetchClientToken: fetchCreateContractSearchClientToken,
    model: "anthropic/claude-sonnet-4.6",
  });

  const handleSearchResultClick = useCallback(
    async (payload: AgentTaskMemoryClickPayload) => {
      try {
        const result = await createMemoryDownloadLink({
          createMemoryDownloadLinkRequest: { memoryId: payload.memoryId },
          organizationId: authState.selectedOrg.id,
          personId: authState.me.id,
        }).unwrap();

        setResultModal({
          downloadLink: result.downloadLink,
          initialPage: payload.pageNumbers[0] ?? 1,
          memoryId: payload.memoryId,
          memoryTitle: payload.memoryTitle,
          searchQuery: query,
        });
      } catch (e) {
        apiError.triggerSoft(e);
      }
    },
    [createMemoryDownloadLink, authState.selectedOrg.id, authState.me.id, apiError, query]
  );

  return (
    <Flex direction="column" h="100%" p="6" gap="4">
      <Text fontSize="xl" fontWeight="semibold">
        {t("Search in contracts")}
      </Text>
      <Text color="smMuted">
        {t("Search across all indexed contract documents in your organization")}
      </Text>

      <Flex
        as="form"
        onSubmit={(e) => {
          e.preventDefault();
          void search(query);
        }}
        gap="2"
      >
        <InputGroup>
          <InputLeftElement pointerEvents="none">
            <SearchIcon color="smTextDisabled" />
          </InputLeftElement>
          <Input
            placeholder={t("Search in contracts") ?? ""}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
        </InputGroup>
        <Button
          type="submit"
          colorScheme="teal"
          isLoading={isSearching}
          isDisabled={query.trim().length === 0}
        >
          {t("Search")}
        </Button>
      </Flex>

      <Box borderWidth="1px" borderColor="smBorder" borderRadius="md" p="4" minH="260px">
        {latest ? (
          <AgentTaskProgress agentTask={latest} mode="verbose" onSearchResultClick={handleSearchResultClick} />
        ) : (
          <Flex align="center" justify="center" h="100%">
            <Text color="smMuted">{t("Enter a search query to find contract content.")}</Text>
          </Flex>
        )}
      </Box>

      {resultModal && (
        <ContractSearchResultModal
          downloadLink={resultModal.downloadLink}
          initialPage={resultModal.initialPage}
          memoryId={resultModal.memoryId}
          memoryTitle={resultModal.memoryTitle}
          initialChatPrompt={resultModal.searchQuery}
          onClose={() => setResultModal(null)}
        />
      )}
    </Flex>
  );
};

export const ContractSearch = () => {
  const { fetchCreateStreamClientToken, fetchCreateContractSearchClientToken } = useFency();

  return (
    <Layout>
      <Grid
        height="full"
        gridTemplateColumns={`1fr minmax(0, ${CONTENT_WIDTH}) 1fr`}
        gridTemplateRows="1fr"
        templateAreas={`"leftContent content rightContent"`}
      >
        <GridItem area="content" px="2">
          <FencyProvider fency={fencyPromise} fetchCreateStreamClientToken={fetchCreateStreamClientToken}>
            <ContractSearchInner fetchCreateContractSearchClientToken={fetchCreateContractSearchClientToken} />
          </FencyProvider>
        </GridItem>
      </Grid>
    </Layout>
  );
};
